import { MicroNotification, type MicroNotificationProps } from '@direct-frontend/components';
import { toast } from 'react-hot-toast';
import cn from 'classnames';
import { createEvent } from '@direct-frontend/stdlib';

import type { ToastType, ToastPosition } from './types';

type ToastEventPayload = {
    type: ToastType;
};

export const defaultPosition: ToastPosition = 'top-center';
export const defaultDurationMs: number = 5000;

const toastEvent = createEvent<ToastEventPayload>();

/**
 * Событие показа уведомления.
 * Возвращает функцию, которую необходимо выполнить при отписке от события.
 */
export const onToastEmit = toastEvent.subscribe;

/**
 * Отозвать уведомление.
 * Уведомление будет скрыто так же, как и по таймеру - с показом анимации и т.д.
 *
 * @param toastId ID уведомления.
 */
export const dismissToast = toast.dismiss;

/**
 * Удалить уведомление немедленно.
 * В отличии от `dismissToast` уведомление пропадет сразу, без показа анимаций.
 *
 * @param toastId ID уведомления.
 */
export const removeToast = toast.remove;

/**
 * Показать информационное уведомление.
 *
 * @param content Содержимое уведомления.
 * @param options Параметры уведомления.
 *
 * @returns ID уведомления.
 */
export const emitInfoToast = createTypeToast('info');

/**
 * Показать уведомление-предупреждение.
 *
 * @param content Содержимое уведомления.
 * @param options Параметры уведомления.
 *
 * @returns ID уведомления.
 */
export const emitWarningToast = createTypeToast('warning');

/**
 * Показать уведомление об ошибке.
 *
 * @param content Содержимое уведомления.
 * @param options Параметры уведомления.
 *
 * @returns ID уведомления.
 */
export const emitErrorToast = createTypeToast('error');

/**
 * Показать уведомление об успехе.
 *
 * @param content Содержимое уведомления.
 * @param options Параметры уведомления.
 *
 * @returns ID уведомления.
 */
export const emitSuccessToast = createTypeToast('success');

/**
 * Показать текстовое уведомление.
 *
 * @param content Содержимое уведомления.
 * @param options Параметры уведомления.
 *
 * @returns ID уведомления.
 */
export const emitMessageToast = createTypeToast('message');

/**
 * Показать уведомление о загрузке.
 *
 * @param content Содержимое уведомления.
 * @param options Параметры уведомления.
 *
 * @returns ID уведомления.
 */
export const emitPendingToast = createTypeToast('pending', { durationMs: Infinity });

/**
 * Дефолтные параметры уведомления для `emitPromiseToast`.
 */
type DefaultPromiseToastOptions = {
    /**
     * Cообщение в процессе ожидания, например: "Загрузка...".
     */
    pending: React.ReactNode;
    /**
     * Сообщение в случае успешного завершения, например: "Загрузка успешно завершена".
     */
    success: React.ReactNode;
    /**
     * Cообщение в случае ошибки, например: "Загрузка завершилась с ошибкой".
     */
    error: React.ReactNode;
};

/**
 * Параметры уведомления для `emitPromiseToast`.
 */
type PromiseToastOptions =
    | DefaultPromiseToastOptions
    | (DefaultPromiseToastOptions & {
          /**
           * Текст для кнопки "повторить", показываемой в случае ошибки.
           */
          retry: string;
          /**
           * Обработчик нажатия на кнопку "повторить", показываемой в случае ошибки.
           */
          onRetry: () => void;
      });

/**
 * Показать серию уведомлений Загрузка -> Успех или Ошибка, привязанных к промису.
 *
 * @param promise Отслеживаемый промис, например запрос данных.
 * @param options Параметры уведомления.
 *
 * @returns Функция отмены показа уведомлений. Отзывает текущее и не показывает все последущие уведомления.
 */
export function emitPromiseToast(promise: Promise<unknown>, options: PromiseToastOptions) {
    let aborted = false;
    let id = emitPendingToast(options.pending);

    promise
        .then(() => {
            if (aborted) {
                return;
            }

            id = emitSuccessToast(options.success, { id });
        })
        .catch(() => {
            if (aborted) {
                return;
            }

            id = emitErrorToast(options.error, {
                id,
                button:
                    'onRetry' in options
                        ? {
                              text: options.retry,
                              onClick: options.onRetry,
                          }
                        : undefined,
            });
        });

    return function abort(method: 'dismiss' | 'remove' = 'dismiss') {
        if (aborted) {
            return;
        }

        aborted = true;

        if (method === 'dismiss') {
            dismissToast(id);
        } else {
            removeToast(id);
        }
    };
}

/**
 * Параметры уведомления.
 */
export type ToastOptions = {
    /**
     * ID уведомления.
     */
    id?: string;
    /**
     * Параметр data-testid для тестов.
     */
    'data-testid'?: string;
    /**
     * Дополнительный поясняющий текст (описание к заголовку).
     */
    caption?: React.ReactNode;
    /**
     * Параметры кнопки действия.
     */
    button?: ButtonOptions | ButtonLinkOptions;
    /**
     * Время показа уведомления.
     */
    durationMs?: number;
    /**
     * Позиция уведомления.
     */
    position?: ToastPosition;
    /**
     * Показывать кнопку закрытия. По умолчанию кнопка показывается, если указано бесконечное время показа.
     */
    hasClose?: boolean;
    /**
     * Обработчик клика по кнопке закрытия.
     */
    onClickClose?: () => void;
    /**
     * Названия классов для элемента
     */
    className?: string;
    /**
     * Иконка слева от текста вместо иконки типа уведомления.
     */
    icon?: MicroNotificationProps['icon'];
};

/**
 * Параметры кнопки действия.
 */
type ButtonOptions = {
    /**
     * Текст кнопки.
     */
    text: string;
    /**
     * Обработчик клика по кнопке.
     */
    onClick: () => void;
};

/**
 * Параметры кнопки действия.
 */
type ButtonLinkOptions = {
    /**
     * Текст кнопки.
     */
    text: string;
    /**
     * URL, если требуется показывать кнопку-ссылку.
     */
    href?: string;
    /**
     * Таргет кнопки-ссылки.
     */
    target?: string;
    /**
     * Обработчик клика по кнопке.
     */
    onClick?: () => void;
};

type ToastTypeOptions = {
    position?: ToastPosition;
    durationMs?: number;
};

function createTypeToast(type: ToastType, defaultTypeOptions: ToastTypeOptions = {}) {
    return (content: React.ReactNode, options: ToastOptions = {}) => {
        const onClose = hasClose(options)
            ? () => {
                  dismissToast(toastId);
                  options?.onClickClose?.();
              }
            : undefined;

        const toastId = String(
            toast(
                ({ id, className }) => (
                    <MicroNotification
                        id={id}
                        className={cn(className, options.className)}
                        type={type}
                        caption={options.caption}
                        buttonProps={options.button}
                        icon={options.icon}
                        // eslint-disable-next-line react/jsx-no-bind -- Ссылка на функцию не поменяется
                        onClose={onClose}
                        data-testid={options['data-testid'] || 'Toast'}
                    >
                        {content}
                    </MicroNotification>
                ),
                {
                    id: options.id,
                    duration: options.durationMs ?? defaultTypeOptions.durationMs ?? defaultDurationMs,
                    position: options.position ?? defaultTypeOptions.position ?? defaultPosition,
                }
            )
        );

        // Нотифицируем подписчиков события onToastEmit
        toastEvent.publish({ type });

        return toastId;
    };
}

function hasClose(options: ToastOptions = {}) {
    if (typeof options.hasClose !== 'undefined') {
        return options.hasClose;
    }

    return options.durationMs === Infinity;
}
